import React, { useState, useEffect } from "react";
import LogoCEA from "../assets/images/logo.png";
import "../style/registermonthly.css";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import SyncLoader from "react-spinners/SyncLoader";
import SizeChartModal from "./SizeChartModal";
import DateInput from "./DateInput";

const classOrder = [
  "OT", "NT", "BD", "BLC", "BCMF", "MASTERCLASS",
  "HH", "CBH", "CBD", "BWG", "MMM", "CONFERENCE",
];

const scholarshipLabels = {
  "need-based": "Opportunity Scholarship",
  merit: "Leadership Scholarship",
};

// Leadership Scholarship-specific questions (merit type only).
const leadershipQuestions = [
  {
    id: "Sejauh ini, bagaimana Anda telah berkontribusi bagi komunitas, baik di dalam maupun di luar gereja lokal? Ceritakan pengalaman pelayanan dan/atau kepemimpinan Anda hingga saat ini.",
    en: "How have you contributed to the community so far, either inside or outside the local church? Tell us about your ministry / leadership experience up until now.",
  },
  {
    id: "Apakah sejauh ini Anda telah mengikuti kelas-kelas kepemimpinan atau pembelajaran Alkitab? Ceritakan pengalaman pembekalan yang telah Anda jalani hingga saat ini.",
    en: "Have you been attending leadership or biblical classes so far? Tell us about your equipping experience up until now.",
  },
  {
    id: "Bagaimana Anda melihat diri Anda dalam 5 hingga 10 tahun ke depan?",
    en: "How do you see yourself in 5 to 10 years?",
  },
  {
    id: "Mengapa Anda memilih Cutting Edge Academy sebagai musim pembekalan dalam perjalanan kepemimpinan dan penatalayanan Anda?",
    en: "Why do you choose Cutting Edge Academy as an equipping season for your journey of leadership and stewardship?",
  },
  {
    id: "Ceritakan tentang holy discontent yang Anda miliki.",
    en: "Tell us about your holy discontent.",
  },
  {
    id: "Ceritakan satu pengalaman yang paling bermakna dalam perjalanan kepemimpinan dan penatalayanan Anda.",
    en: "Tell us about one significant experience that you have during your journey of leadership and stewardship.",
  },
  {
    id: "Mengapa kami seharusnya memberikan beasiswa ini kepada Anda?",
    en: "Why should we give this scholarship to you?",
  },
];

// Opportunity Scholarship-specific questions (need-based type only).
const opportunityQuestions = [
  {
    id: "Bagaimana Anda menggambarkan kondisi Anda saat ini?",
    en: "How would you describe your condition currently?",
  },
  {
    id: "Seberapa mendesak kebutuhan Anda untuk bergabung dengan Cutting Edge Academy pada tahun ini?",
    en: "What is your urgency of joining Cutting Edge Academy this year?",
  },
  {
    id: "Berapa besar dukungan finansial yang Anda ajukan agar dapat bergabung dengan Cutting Edge Academy pada tahun ini?",
    en: "How big is the financial support you propose to receive in order to join Cutting Edge Academy this year?",
  },
  {
    id: "Ceritakan pengalaman pelayanan dan/atau pengabdian kepada masyarakat yang telah Anda jalani hingga saat ini.",
    en: "Tell us about your ministry / community service experience you have up until now.",
  },
  {
    id: "Mengapa kami seharusnya memberikan beasiswa ini kepada Anda?",
    en: "Why should we give this scholarship to you?",
  },
];

const ApplyScholarship = ({ classDetails = [] }) => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Processing...");
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [currentClass, setCurrentClass] = useState("");
  const [batchNum, setBatchNum] = useState(() => {
    const now = new Date();
    return now.getMonth() >= 8 ? now.getFullYear() - 2021 : now.getFullYear() - 2022;
  });

  useEffect(() => {
    axios.get("/config/currentclass")
      .then((res) => { if (res.data.currentClass) setCurrentClass(res.data.currentClass); })
      .catch(() => {});
    // Batch mengikuti "Current Running Batch" yang di-set admin (SIMULATED_BATCH),
    // bukan sekadar hitungan kalender — supaya konsisten dgn RegisterYearly.
    axios.get("/config/simulatedbatch")
      .then((res) => { if (res.data.simulatedBatch) setBatchNum(parseInt(res.data.simulatedBatch)); })
      .catch(() => {});
  }, []);

  // Step 0: joined CEA?
  const [joinedCEA, setJoinedCEA] = useState("");
  const [yearJoined, setYearJoined] = useState("");
  const [emailVerify, setEmailVerify] = useState("");
  // Classes already completed (e.g. as an existing 1 Month Course student) —
  // these must not be re-listed as "classes you will take".
  const [existingClasses, setExistingClasses] = useState([]);

  // Step 1: enrollment fields
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [postal, setPostal] = useState("");
  const [sp, setSp] = useState("");
  const [church, setChurch] = useState("");
  const [otherChurch, setOtherChurch] = useState("");
  const [dj, setDj] = useState("");
  const [role, setRole] = useState("");
  const [classType, setClassType] = useState("");
  const [tshirtSize, setTshirtSize] = useState("");
  const [startingClass, setStartingClass] = useState("OT");
  // Kelas sebelum current class (config admin) sudah lewat → disable di Starting Class.
  const isClassPassed = (code) => {
    const currentIdx = classOrder.indexOf(currentClass);
    return currentIdx > 0 && classOrder.indexOf(code) < currentIdx;
  };
  useEffect(() => {
    if (!isClassPassed(startingClass)) return;
    const firstOpen = classOrder.find((code) => !isClassPassed(code));
    if (firstOpen) setStartingClass(firstOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClass, startingClass]);
  // Sengaja TANPA default: applicant harus memilih sendiri cara bayarnya, supaya
  // tidak ada yang ter-submit "full" hanya karena pertanyaannya terlewat dibaca.
  const [paymentPlan, setPaymentPlan] = useState("");
  const [q1, setQ1] = useState("");
  const [q2, setQ2] = useState("");
  const [q3, setQ3] = useState("");
  const [q4, setQ4] = useState("");
  const [q5, setQ5] = useState("");

  // Step 2 (Leadership/merit): 7 questions
  const [meritAnswers, setMeritAnswers] = useState(Array(leadershipQuestions.length).fill(""));
  // Step 2 (Opportunity/need-based): 5 questions
  const [opportunityAnswers, setOpportunityAnswers] = useState(Array(opportunityQuestions.length).fill(""));
  // Shared by both: reference contact
  const [referenceName, setReferenceName] = useState("");
  const [referenceContact, setReferenceContact] = useState("");

  const updateMeritAnswer = (index, value) => {
    setMeritAnswers((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const updateOpportunityAnswer = (index, value) => {
    setOpportunityAnswers((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  useEffect(() => {
    if (!state?.type) navigate("/scholarshipinfo", { replace: true });
  }, [state, navigate]);

  if (!state?.type) return null;

  const scholarshipType = state.type;
  const scholarshipLabelText = state.label || scholarshipLabels[state.type] || state.type;

  // Kelas yang sudah pernah diambil (mis. dari 1 Month Course) tidak boleh
  // ditampilkan/didaftarkan ulang sebagai "classes you will take".
  const completedClasses = existingClasses.filter((c) => classOrder.includes(c.name));
  const completedNames = completedClasses.map((c) => c.name);
  const remainingClasses = classOrder
    .filter((c) => !completedNames.includes(c))
    .map((c) => ({ name: c, status: "requested" }));
  const finalClassList = [...completedClasses, ...remainingClasses].sort(
    (a, b) => classOrder.indexOf(a.name) - classOrder.indexOf(b.name),
  );

  // Preview batch & kelas realtime berdasarkan running class saat ini + Starting
  // Class yang dipilih applicant — mirrors CeaToChurch.js's renderYearlyBatchPreview
  // exactly (late-joiner wrap-around: kelas sebelum startingClass digeser ke batch
  // berikutnya, bukan diasumsikan selalu mulai dari OT di batch yang sama).
  const renderBatchPreview = () => {
    const requestedClasses = finalClassList.filter((c) => c.status !== "completed");
    const startIdx = classOrder.indexOf(startingClass);
    const currentClassIdx = classOrder.indexOf(currentClass);
    const isLateForCurrentBatch = currentClassIdx > 0 && startIdx < currentClassIdx;

    const renderList = (items) =>
      items.map((item) => {
        const detail = classDetails.find((c) => c.code === item.name);
        return <li key={item.name}>{detail ? detail.name : item.name}</li>;
      });

    const completedBlock = completedClasses.length > 0 && (
      <div className="custom-margin-form mt-4">
        <label className="form-label">Classes Already Completed</label>
        <ol>{renderList(completedClasses)}</ol>
      </div>
    );

    if (isLateForCurrentBatch) {
      const lateNextBatchClasses = requestedClasses.filter((c) => classOrder.indexOf(c.name) >= startIdx);
      const lateBatchPlusTwoClasses = requestedClasses.filter((c) => classOrder.indexOf(c.name) < startIdx);
      return (
        <>
          {completedBlock}
          <div className="custom-margin-form mt-4">
            <label className="form-label">Classes to follow in Batch {batchNum + 1}</label>
            <ol>{renderList(lateNextBatchClasses)}</ol>
          </div>
          {lateBatchPlusTwoClasses.length > 0 && (
            <div className="custom-margin-form mt-4">
              <label className="form-label">Classes to follow in Batch {batchNum + 2}</label>
              <ol>{renderList(lateBatchPlusTwoClasses)}</ol>
            </div>
          )}
        </>
      );
    }

    const currentBatchRequested = requestedClasses.filter((c) => classOrder.indexOf(c.name) >= startIdx);
    const nextBatchRequested = requestedClasses.filter((c) => classOrder.indexOf(c.name) < startIdx);

    if (nextBatchRequested.length > 0) {
      return (
        <>
          {completedBlock}
          <div className="custom-margin-form mt-4">
            <label className="form-label">Classes to follow in Batch {batchNum}</label>
            <ol>{renderList(currentBatchRequested)}</ol>
          </div>
          <div className="custom-margin-form mt-4">
            <label className="form-label">Classes to follow in Batch {batchNum + 1}</label>
            <ol>{renderList(nextBatchRequested)}</ol>
          </div>
        </>
      );
    }

    return (
      <>
        {completedBlock}
        <div className="custom-margin-form mt-4">
          <label className="form-label">Classes to follow in Batch {batchNum}</label>
          <ol>{renderList(requestedClasses)}</ol>
        </div>
      </>
    );
  };

  const showError = async (text) => {
    await Swal.fire({
      text,
      icon: "error",
      background: "white",
      color: "black",
      backdrop: "rgba(131, 131, 131, 0.5)",
      confirmButtonColor: "#fb3104",
      confirmButtonText: "OK",
    });
  };

  const goBack = () => {
    if (step === 0) navigate("/scholarshipinfo");
    else { setStep((s) => s - 1); window.scrollTo(0, 0); }
  };

  const clearEnrollmentFields = () => {
    setFullName("");
    setEmail("");
    setPhone("");
    setDob("");
    setAddress("");
    setCity("");
    setProvince("");
    setPostal("");
    setSp("");
    setChurch("");
    setOtherChurch("");
    setDj("");
    setRole("");
    setClassType("");
    setTshirtSize("");
    setYearJoined("");
    setQ1("");
    setQ2("");
    setQ3("");
    setQ4("");
    setQ5("");
    setExistingClasses([]);
  };

  // ── Step 0 → Step 1 ──
  const handleStep0Next = async () => {
    if (!joinedCEA) { await showError("Please input all data!"); return; }

    if (joinedCEA === "No") {
      clearEnrollmentFields();
      setStep(1);
      window.scrollTo(0, 0);
      return;
    }

    // joinedCEA === "Yes": verify by email
    if (!emailVerify) { await showError("Please input all data!"); return; }

    setLoadingText("Loading your data...");
    setIsLoading(true);
    try {
      const response = await axios.get(`/verifydata/${emailVerify}`);
      setIsLoading(false);

      if (response.data === null) {
        await showError(
          "Email ini belum pernah terdaftar di Cutting Edge Academy. Silakan isi formulir sebagai student baru."
        );
        clearEnrollmentFields();
        // Email tidak ditemukan → perlakukan sebagai pendaftar BARU supaya field
        // Confirm Email (kondisi joinedCEA "No") ikut muncul.
        setJoinedCEA("No");
        setStep(1);
        window.scrollTo(0, 0);
      } else if (response.data.program === "1 Year Program") {
        await showError(
          "Email ini sudah terdaftar sebagai student 1 Year Program. Silakan gunakan email lain."
        );
      } else if (response.data.msg === "Your data still in approval process.") {
        await Swal.fire({
          text: response.data.msg,
          icon: "info",
          background: "white",
          color: "black",
          backdrop: "rgba(131, 131, 131, 0.5)",
          confirmButtonColor: "#fb3104",
          confirmButtonText: "OK",
        });
      } else {
        const d = response.data;
        setFullName(d.name || "");
        setEmail(d.email || "");
        setPhone(d.phoneNumber || "");
        setDob(d.dateOfBirth || "");
        setAddress(d.address || "");
        setCity(d.city || "");
        setProvince(d.province || "");
        setPostal(d.postalCode || "");
        setSp(d.studentPackage || "");
        if (d.localChurch === "IFGF Bandung") {
          setChurch("IFGF Bandung");
          setRole(d.role || "");
          setDj(d.lastDj || "");
        } else if (d.localChurch) {
          setChurch("Others");
          setOtherChurch(d.localChurch);
        }
        setClassType(d.classType || "");
        setTshirtSize(d.tshirtSize || "");
        setYearJoined(d.yearJoined || "");
        setQ1(d.question1 || "");
        setQ2(d.question2 || "");
        setQ3(d.question3 || "");
        setQ4(d.question4 || "");
        setQ5(d.question5 || "");
        if (d.program === "1 Month Course" && d.classList) {
          const parsedClassList =
            typeof d.classList === "string" ? JSON.parse(d.classList) : d.classList;
          setExistingClasses(parsedClassList.className || []);
        } else {
          setExistingClasses([]);
        }
        setStep(1);
        window.scrollTo(0, 0);
      }
    } catch (error) {
      setIsLoading(false);
      await showError(error.response?.data?.msg || "Something went wrong.");
    }
  };

  // ── Step 1 → Step 2 ──
  const handleNext = () => {
    const localChurchVal = church === "IFGF Bandung" ? "IFGF Bandung" : otherChurch.trim();
    const churchOk =
      church !== "" &&
      (church === "Others" ? localChurchVal !== "" : dj !== "" && role !== "");

    if (
      !fullName || !email || !phone || !dob || !address || !city ||
      !province || !postal || !sp || !churchOk || !classType ||
      !paymentPlan || !tshirtSize || !q1 || !q2 || !q3 || !q4 || !q5
    ) {
      showError("Please input all data!");
      return;
    }
    setStep(2);
    window.scrollTo(0, 0);
  };

  // ── Step 2: Final submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();

    const answers = scholarshipType === "merit" ? meritAnswers : opportunityAnswers;
    if (answers.some((a) => !a) || !referenceName || !referenceContact) {
      await showError("Please input all data!");
      return;
    }

    // Cegah salah ketik email untuk pendaftar BARU (joinedCEA "No").
    if (joinedCEA === "No" && email.trim().toLowerCase() !== confirmEmail.trim().toLowerCase()) {
      await showError("Email and Confirm Email do not match. Please re-check your email address.");
      return;
    }

    const result = await Swal.fire({
      text: "Are you sure you want to submit this application?",
      icon: "question",
      background: "white",
      color: "black",
      backdrop: "rgba(131, 131, 131, 0.5)",
      showCancelButton: true,
      confirmButtonColor: "#fb3104",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
    });
    if (!result.isConfirmed) return;

    setLoadingText("Processing your application...");
    setIsLoading(true);
    const localChurch = church === "IFGF Bandung" ? "IFGF Bandung" : otherChurch.trim();

    try {
      await axios.post("/scholarship", {
        scholarshipType,
        name: fullName,
        email,
        phoneNumber: phone,
        dateOfBirth: dob,
        address,
        city,
        province,
        postalCode: postal,
        studentPackage: sp,
        localChurch,
        lastDj: dj,
        role,
        classType,
        tshirtSize,
        paymentPlan,
        classList: { className: finalClassList },
        currentClass,
        startingClass,
        joinedCEA,
        yearJoined: joinedCEA === "Yes" ? yearJoined : "",
        question1: q1,
        question2: q2,
        question3: q3,
        question4: q4,
        question5: q5,
        ...(scholarshipType === "merit"
          ? {
              scholarshipQ1: meritAnswers[0],
              scholarshipQ2: meritAnswers[1],
              scholarshipQ3: meritAnswers[2],
              scholarshipQ4: meritAnswers[3],
              scholarshipQ5: meritAnswers[4],
              scholarshipQ6: meritAnswers[5],
              scholarshipQ7: meritAnswers[6],
            }
          : {
              scholarshipQ1: opportunityAnswers[0],
              scholarshipQ2: opportunityAnswers[1],
              scholarshipQ3: opportunityAnswers[2],
              scholarshipQ4: opportunityAnswers[3],
              scholarshipQ5: opportunityAnswers[4],
            }),
        referenceName,
        referenceContact,
      });

      setIsLoading(false);

      const confirmBtn = await Swal.fire({
        text: "Your scholarship application has been submitted successfully!",
        icon: "success",
        background: "white",
        color: "black",
        backdrop: "rgba(131, 131, 131, 0.5)",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "OK",
      });

      if (confirmBtn.isConfirmed) navigate("/scholarshipinfo");
    } catch (error) {
      setIsLoading(false);
      await showError(error.response?.data?.msg || "Something went wrong.");
    }
  };

  // ── Shared panel transform helpers ──
  const p0Transform = step === 0 ? "translate(-50%, 0)" : "translate(-200%, 0)";
  const p1Transform =
    step < 1 ? "translate(200%, 15rem)" :
    step === 1 ? "translate(-50%, 15rem)" :
    "translate(-200%, 15rem)";
  const p2Transform = step < 2 ? "translate(200%, 15rem)" : "translate(-50%, 15rem)";

  return (
    <main
      className="position-relative"
      style={{
        height: "100vh",
        overflowX: "clip",
        overflowY: step === 0 ? "hidden" : "visible",
      }}
    >
      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0, left: 0,
            width: "100%", height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <SyncLoader color={"#fb3104"} size={12} />
          <p style={{ color: "white", marginTop: "1.5rem", fontSize: "1em", fontWeight: "500" }}>
            {loadingText}
          </p>
        </div>
      )}

      <div className="custom-con-nav-register position-relative top-0 start-50 translate-middle-x">
        <div className="custom-con-img-regisform position-absolute top-50 end-0 translate-middle-y">
          <img className="adjust-img-register object-fit-contain" src={LogoCEA} alt="logoCEA.png" />
        </div>
        <i
          className="custom-icon-arrow-register bi bi-arrow-left position-absolute top-50 start-0 translate-middle-y"
          onClick={goBack}
        />
      </div>

      <p className="custom-title-register-monthly text-center">
        {scholarshipLabelText} — Application Form
      </p>

      {/* ══ Panel 0: Have you ever joined CEA? ══ */}
      <div
        className="custom-con-form1-monthly position-relative top-0 start-50"
        style={{ transform: p0Transform }}
      >
        <form onSubmit={(e) => { e.preventDefault(); handleStep0Next(); }}>
          <div className="d-flex flex-column mb-3">
            <label className="form-label">
              Have you ever joined Cutting Edge Academy before?
            </label>
            <label className="custom-radio-space">
              <input
                type="radio"
                name="joinedCEA"
                value="Yes"
                checked={joinedCEA === "Yes"}
                onChange={(e) => setJoinedCEA(e.target.value)}
              />
              &nbsp;Yes
            </label>
            <label className="custom-radio-space">
              <input
                type="radio"
                name="joinedCEA"
                value="No"
                checked={joinedCEA === "No"}
                onChange={(e) => { setJoinedCEA(e.target.value); setEmailVerify(""); }}
              />
              &nbsp;No
            </label>
          </div>

          {joinedCEA === "Yes" && (
            <div className="custom-margin-form">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control custom-con-input"
                placeholder="example@gmail.com"
                value={emailVerify}
                onChange={(e) => setEmailVerify(e.target.value)}
              />
            </div>
          )}

          {joinedCEA !== "" && (
            <button type="submit" className="btn custom-btn-next">
              Next
            </button>
          )}
        </form>
      </div>

      {/* ══ Panel 1: Enrollment Form ══ */}
      <div
        className="custom-con-form2-monthly position-absolute top-0 start-50"
        style={{ transform: p1Transform }}
      >
        <form onSubmit={(e) => { e.preventDefault(); handleNext(); }}>
          <div className="custom-margin-form">
            <label className="form-label">Full Name</label>
            <input
              type="text"
              className="form-control custom-con-input"
              placeholder="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>

          <div className="custom-margin-form">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control custom-con-input"
              placeholder="example@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {joinedCEA === "No" && (
            <div className="custom-margin-form">
              <label className="form-label">Confirm Email</label>
              <input
                type="email"
                className="form-control custom-con-input"
                placeholder="Re-type your email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                onPaste={(e) => e.preventDefault()}
              />
              {confirmEmail !== "" &&
                email.trim().toLowerCase() !== confirmEmail.trim().toLowerCase() && (
                  <span style={{ color: "#dc2626", fontSize: "12px" }}>
                    Email does not match
                  </span>
                )}
            </div>
          )}

          <div className="custom-margin-form">
            <label className="form-label">Whatsapp Number</label>
            <input
              type="text"
              className="form-control custom-con-input"
              placeholder="6287xxxxxxxxx"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
            />
          </div>

          <div className="custom-margin-form">
            <label className="form-label">Date of Birth</label>
            <DateInput
              className="form-control custom-con-input"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
            />
          </div>

          <div className="custom-margin-form">
            <label className="form-label">Address (Indonesia Address)</label>
            <textarea
              rows="2"
              className="form-control form-control-textarea"
              placeholder="Insert your address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          <div className="custom-margin-form">
            <label className="form-label">City</label>
            <input
              type="text"
              className="form-control custom-con-input"
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>

          <div className="row align-items-center">
            <div className="col-6">
              <div className="custom-margin-form">
                <label className="form-label">Province</label>
                <input
                  type="text"
                  className="form-control custom-con-input"
                  placeholder="Province"
                  value={province}
                  onChange={(e) => setProvince(e.target.value)}
                />
              </div>
            </div>
            <div className="col-6">
              <div className="custom-margin-form">
                <label className="form-label">Postal Code</label>
                <input
                  type="text"
                  className="form-control custom-con-input"
                  placeholder="Postal Code"
                  value={postal}
                  onChange={(e) => setPostal(e.target.value.replace(/\D/g, ""))}
                />
              </div>
            </div>
          </div>

          <div className="d-flex flex-column mt-4">
            <label className="form-label">Student Package</label>
            <label className="custom-radio-space">
              <input
                type="radio"
                name="sp"
                value="The House Convention Hall"
                checked={sp === "The House Convention Hall"}
                onChange={(e) => setSp(e.target.value)}
              />
              &nbsp;The House Convention Hall
            </label>
            <label className="custom-radio-space">
              <input
                type="radio"
                name="sp"
                value="Shipping"
                checked={sp === "Shipping"}
                onChange={(e) => setSp(e.target.value)}
              />
              &nbsp;Shipping (only for student outside Bandung)
            </label>
          </div>

          <div className="custom-margin-form mt-4">
            <label className="form-label">Church</label>
            <select
              className="form-select custom-con-input"
              value={church}
              onChange={(e) => setChurch(e.target.value)}
            >
              <option value="">Select Local Church</option>
              <option value="IFGF Bandung">IFGF Bandung</option>
              <option value="Others">Others</option>
            </select>
          </div>

          {church === "IFGF Bandung" && (
            <>
              <div className="custom-margin-form mt-4">
                <label className="form-label">Last DJ Class</label>
                <select
                  className="form-select custom-con-input"
                  value={dj}
                  onChange={(e) => setDj(e.target.value)}
                >
                  <option value="">Select DJ Class</option>
                  <option>Come</option>
                  <option>Grow</option>
                  <option>Serve</option>
                  <option>Lead</option>
                  <option>None</option>
                </select>
              </div>
              <div className="custom-margin-form mt-4">
                <label className="form-label">Role</label>
                <select
                  className="form-select custom-con-input"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                >
                  <option value="">Select Role</option>
                  <option>HITS/Mth Student</option>
                  <option>Pastoral</option>
                  <option>Staff</option>
                  <option>Coach</option>
                  <option>Care Group Leader (CGL)</option>
                  <option>Core Team Leader (CTL)</option>
                  <option>Attendees (IFGF BDG)</option>
                </select>
              </div>
            </>
          )}

          {church === "Others" && (
            <div className="custom-margin-form">
              <label className="form-label">Local Church</label>
              <input
                type="text"
                className="form-control custom-con-input"
                placeholder="Input Local Church"
                value={otherChurch}
                onChange={(e) => setOtherChurch(e.target.value)}
              />
            </div>
          )}

          <div className="d-flex flex-column mt-5">
            <label className="form-label">Online / Onsite</label>
            <label className="custom-radio-space">
              <input
                type="radio"
                name="classType"
                value="Online"
                checked={classType === "Online"}
                onChange={(e) => setClassType(e.target.value)}
              />
              &nbsp;Online
            </label>
            <label className="custom-radio-space">
              <input
                type="radio"
                name="classType"
                value="Onsite"
                checked={classType === "Onsite"}
                onChange={(e) => setClassType(e.target.value)}
              />
              &nbsp;Onsite
            </label>
          </div>

          <div className="custom-margin-form mt-4">
            <label className="form-label">Programs</label>
            <select className="form-select custom-con-input" disabled>
              <option>1 Year Program</option>
            </select>
          </div>

          <div className="custom-margin-form mt-4">
            <label className="form-label">Starting Class</label>
            <select
              className="form-select custom-con-input"
              value={startingClass}
              onChange={(e) => setStartingClass(e.target.value)}
            >
              {classOrder.map((code) => {
                const detail = classDetails.find((d) => d.code === code);
                return (
                  <option key={code} value={code} disabled={isClassPassed(code)}>
                    {detail ? detail.name : code}
                  </option>
                );
              })}
            </select>
          </div>

          {renderBatchPreview()}

          <div className="d-flex flex-column mt-4">
            <label className="form-label">Payment Option</label>
            <label className="custom-radio-space">
              <input
                type="radio"
                name="paymentPlan"
                value="full"
                checked={paymentPlan === "full"}
                onChange={(e) => setPaymentPlan(e.target.value)}
              />
              &nbsp;Full Payment (pay the whole program fee at once, in a single
              payment now)
            </label>
            <label className="custom-radio-space">
              <input
                type="radio"
                name="paymentPlan"
                value="installment"
                checked={paymentPlan === "installment"}
                onChange={(e) => setPaymentPlan(e.target.value)}
              />
              &nbsp;Installment (pay a 2-class down payment now, remaining classes billed monthly)
            </label>
          </div>

          <div className="custom-margin-form mt-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-1">
              <label className="form-label mb-0">Tshirt Size</label>
              <span
                className="size-chart-trigger"
                onClick={() => setShowSizeChart(true)}
              >
                📏 Size Chart
              </span>
            </div>
            <select
              className="form-select custom-con-input"
              value={tshirtSize}
              onChange={(e) => setTshirtSize(e.target.value)}
            >
              <option value="">Select Size</option>
              <option value="s">S</option>
              <option value="m">M</option>
              <option value="l">L</option>
              <option value="xl">XL</option>
              <option value="xxl">XXL</option>
            </select>
          </div>

          <div className="custom-margin-form mt-4">
            <label className="form-label">
              Bagaimana kisah perjalanan kekristenan Anda, ketika Anda mengalami lahir baru hingga saat ini?
            </label>
            <textarea
              rows="2"
              className="form-control form-control-textarea"
              value={q1}
              onChange={(e) => setQ1(e.target.value)}
            />
          </div>
          <div className="custom-margin-form">
            <label className="form-label">Apakah Anda sudah mengalami baptisan?</label>
            <textarea
              rows="1"
              className="form-control form-control-textarea"
              value={q2}
              onChange={(e) => setQ2(e.target.value)}
            />
          </div>
          <div className="custom-margin-form">
            <label className="form-label">
              Ceritakan beberapa pengalaman pelayanan Anda dan buah dari pelayanan-pelayanan tersebut.
            </label>
            <textarea
              rows="2"
              className="form-control form-control-textarea"
              value={q3}
              onChange={(e) => setQ3(e.target.value)}
            />
          </div>
          <div className="custom-margin-form">
            <label className="form-label">
              Apa rencana dan langkah Anda selanjutnya setelah mengikuti Cutting Edge Academy?
            </label>
            <textarea
              rows="2"
              className="form-control form-control-textarea"
              value={q4}
              onChange={(e) => setQ4(e.target.value)}
            />
          </div>
          <div className="custom-margin-form">
            <label className="form-label">
              Mengapa Anda ingin bergabung dengan Cutting Edge Academy?
            </label>
            <textarea
              rows="2"
              className="form-control form-control-textarea"
              value={q5}
              onChange={(e) => setQ5(e.target.value)}
            />
          </div>

          <button type="submit" className="btn custom-btn-next" style={{ marginBottom: "60px" }}>
            Next
          </button>
        </form>
      </div>

      {/* ══ Panel 2: Scholarship Questions ══ */}
      <div
        className="custom-con-form2-monthly position-absolute top-0 start-50"
        style={{ transform: p2Transform }}
      >
        <form onSubmit={handleSubmit}>
          <div
            style={{
              padding: "12px 16px",
              backgroundColor: "#fff0ee",
              borderRadius: "8px",
              marginBottom: "20px",
              borderLeft: "4px solid #fb3104",
            }}
          >
            <p style={{ margin: 0, fontSize: "13px", color: "#fb3104", fontWeight: "700" }}>
              {scholarshipLabelText}
            </p>
          </div>

          {(scholarshipType === "merit" ? leadershipQuestions : opportunityQuestions).map(
            (question, index) => (
              <div className="custom-margin-form" key={index}>
                <label className="form-label">
                  {question.id}
                  <br />
                  <span style={{ fontSize: "12px", color: "#888", fontWeight: "400" }}>
                    ({question.en})
                  </span>
                </label>
                <textarea
                  rows="4"
                  className="form-control form-control-textarea"
                  value={scholarshipType === "merit" ? meritAnswers[index] : opportunityAnswers[index]}
                  onChange={(e) =>
                    scholarshipType === "merit"
                      ? updateMeritAnswer(index, e.target.value)
                      : updateOpportunityAnswer(index, e.target.value)
                  }
                />
              </div>
            ),
          )}

          <div
            style={{
              marginTop: "48px",
              paddingTop: "28px",
              borderTop: "1px solid #eee",
            }}
          >
            <div className="custom-margin-form">
              <label className="form-label">
                Silahkan lengkapi informasi kontak Pemberi Referensi Anda (non-keluarga) yang berkaitan dengan pengajuan beasiswa ini.
              </label>
            </div>
          </div>
          <div className="custom-margin-form">
            <label className="form-label">Nama</label>
            <input
              type="text"
              className="form-control custom-con-input"
              value={referenceName}
              onChange={(e) => setReferenceName(e.target.value)}
            />
          </div>
          <div className="custom-margin-form">
            <label className="form-label">Nomor Kontak</label>
            <input
              type="text"
              className="form-control custom-con-input"
              value={referenceContact}
              onChange={(e) => setReferenceContact(e.target.value.replace(/\D/g, ""))}
            />
          </div>

          <button type="submit" className="btn custom-btn-regis">
            Submit Application
          </button>
        </form>
      </div>
      <SizeChartModal show={showSizeChart} onClose={() => setShowSizeChart(false)} />
    </main>
  );
};

export default ApplyScholarship;
